import { useCallback, useMemo, useState, useEffect } from "react";
import { Table, TableBody, TableFooter } from "@mui/material";
import { hierarchyTableStyles } from "../../Styles/hierarchyTableStyles";
import type { HierarchyTableProps, HierarchyLevel } from "../../Types/types";
import { HierarchyTableHeader } from "./HierarchyTableHeader";
import { HierarchyTableRow } from "./HierarchyTableRow";
import { HierarchyTableTotalRow } from "./HierarchyTableTotalRow";
import { collectExpandableIds, flattenVisibleRows } from "./utils";

export const HierarchyTable = ({
  data,
  total,
  dateStart,
  dateEnd,
  defaultExpandedIds,
  onExpandNode,
  loadingIds,
}: HierarchyTableProps) => {
  const initialExpanded = useMemo(
    () => new Set(defaultExpandedIds ?? collectExpandableIds(data, 0)),
    [data, defaultExpandedIds],
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(initialExpanded);

  const rootId = data[0]?.entity_id;

  useEffect(() => {
    if (rootId) {
      setExpandedIds(new Set([rootId]));
    }
  }, [rootId]);

  const handleToggle = useCallback(
    (entityId: string, level: HierarchyLevel) => {
      const isExpanding = !expandedIds.has(entityId);
      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (next.has(entityId)) {
          next.delete(entityId);
        } else {
          next.add(entityId);
        }
        return next;
      });

      if (isExpanding && onExpandNode) {
        onExpandNode(entityId, level);
      }
    },
    [expandedIds, onExpandNode],
  );

  const visibleRows = useMemo(
    () => flattenVisibleRows(data, expandedIds),
    [data, expandedIds],
  );

  return (
    <Table size="small" sx={hierarchyTableStyles.table}>
      <HierarchyTableHeader dateStart={dateStart} dateEnd={dateEnd} />
      <TableBody>
        {visibleRows.map(({ node, depth, rowKey }) => (
          <HierarchyTableRow
            key={rowKey}
            node={node}
            depth={depth}
            isExpanded={expandedIds.has(node.entity_id)}
            onToggle={handleToggle}
            isLoading={loadingIds?.has(node.entity_id)}
          />
        ))}
      </TableBody>
      {total && (
        <TableFooter>
          <HierarchyTableTotalRow total={total} />
        </TableFooter>
      )}
    </Table>
  );
};
